'use client'

import React, { useEffect, useState } from 'react'
import { OffersOffer } from '@/services/dataTypes'
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks'
import { apiCategoryOfferBanners } from '@/apis/user'
import Banner from './Banner'
import { ChevronDown, ChevronUp, Loader2, Tag } from 'lucide-react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', BROWN: '#8C5826', BORDER: '#ffe0c2', LIGHT: '#E1F1F2' }

interface Props {
    bannerResponse: OffersOffer[]
    domain: string
    mer_slug: string
    slug_type: string
    categoryId: string
    companyId: string
}

const VerticalCategoryOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, categoryId, companyId }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([])
    const [page, setPage] = useState(2)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000))
    }, [bannerResponse])

    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const res = await apiCategoryOfferBanners(categoryId, companyId, page)
            const newBanners = filterOfferBanners(res.data?.offers || [], 50, 2000, 65, 2000)
            setBanners(prev => [...prev, ...newBanners])
            if (res?.data?.pagination?.next_page) {
                setPage(prev => prev + 1)
                setIsExpanded(true)
            } else {
                setHasMore(false)
            }
        } catch (e) {
            console.error('Error loading banners', e)
        } finally {
            setLoading(false)
        }
    }

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000))
        setPage(2)
        setHasMore(true)
        setIsExpanded(false)
    }

    if (!banners?.length) return null

    return (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: `1px solid ${C.BORDER}` }}>
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3" style={{ background: C.BROWN }}>
                <Tag size={13} style={{ color: C.PRIMARY }} />
                <h4 className="text-xs font-black uppercase tracking-wider" style={{ color: C.PRIMARY }}>Offer Banners</h4>
            </div>

            {/* Banners */}
            <div className="p-3 space-y-2">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data)
                    return (
                        <Banner
                            key={i}
                            data={offer_data}
                            height={dimension?.height}
                            width={dimension?.width}
                            mer_slug={mer_slug}
                            slug_type={slug_type}
                            domain={domain}
                        />
                    )
                })}
            </div>

            {/* Load more / Show less */}
            <div className="flex items-center gap-2 px-3 pb-3">
                {hasMore && (
                    <button onClick={loadMore} disabled={loading}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-xl transition-all disabled:opacity-50"
                        style={{ background: C.LIGHT, color: C.BROWN }}>
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronDown size={12} />}
                        {loading ? 'Loading...' : 'Show More'}
                    </button>
                )}
                {isExpanded && (
                    <button onClick={showLess}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-xl transition-all"
                        style={{ background: `${C.BROWN}10`, color: `${C.BROWN}80` }}>
                        <ChevronUp size={12} /> Show Less
                    </button>
                )}
            </div>
        </div>
    )
}

export default VerticalCategoryOfferBanner
